import React from "react";
import {
  Lightbulb,
  AlertCircle,
  BookOpen,
  Calendar,
  CheckCircle,
  Target,
  Clock,
  ArrowRight,
  Star,
} from "lucide-react";

const RecommendationsFeedbacksSection = ({
  recommendations = [],
  feedbacks = [],
}) => {
  const getPriorityConfig = (priority) => {
    const configs = {
      high: {
        bg: "bg-red-50",
        border: "border-red-200",
        textColor: "text-red-700",
        iconColor: "text-red-600",
        badge: "High Priority",
      },
      medium: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        textColor: "text-amber-700",
        iconColor: "text-amber-600",
        badge: "Medium Priority",
      },
      low: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        textColor: "text-blue-700",
        iconColor: "text-blue-600",
        badge: "Low Priority",
      },
    };

    return configs[priority] || configs.medium;
  };

  const getSeverityConfig = (severity) => {
    const configs = {
      high: {
        bg: "bg-red-50",
        border: "border-red-200",
        icon: AlertCircle,
        iconColor: "text-red-600",
      },
      medium: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        icon: AlertCircle,
        iconColor: "text-amber-600",
      },
      low: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: CheckCircle,
        iconColor: "text-blue-600",
      },
    };

    return configs[severity] || configs.medium;
  };

  const getActionIcon = (actionType) => {
    const icons = {
      schedule: Calendar,
      practice: BookOpen,
      review: Clock,
      target: Target,
    };

    return icons[actionType] || Calendar;
  };

  return (
    <div className="space-y-4">
      {/* Recommendations */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Recommendations
            </h2>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Personalized suggestions for improvement
          </p>
        </div>

        <div className="space-y-3">
          {recommendations.map((rec, index) => {
            const priorityConfig = getPriorityConfig(rec.priority);
            const ActionIcon = getActionIcon(rec.action?.type);

            return (
              <div
                key={index}
                className={`${priorityConfig.bg} ${priorityConfig.border} border rounded-lg p-3`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-2 flex-1">
                    <Lightbulb
                      className={`w-4 h-4 mt-0.5 ${priorityConfig.iconColor}`}
                    />

                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {rec.title}
                      </h3>

                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-medium ${priorityConfig.bg} ${priorityConfig.textColor}`}
                        >
                          {priorityConfig.badge}
                        </span>

                        <span className="text-[10px] text-gray-500 capitalize">
                          {rec.type?.replace("_", " ")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                  {rec.description}
                </p>

                {rec.action && (
                  <button className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-md border border-gray-200 hover:bg-gray-50 transition-colors">
                    <ActionIcon className="w-3.5 h-3.5 text-amber-600" />

                    <span className="text-xs font-medium text-gray-700">
                      {rec.action.value
                        .replace("_", " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </span>

                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Feedbacks */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Feedback</h2>
          </div>

          <p className="text-xs text-gray-500 mt-1">
            Detailed performance insights
          </p>
        </div>

        <div className="space-y-3">
          {feedbacks.map((feedback, index) => {
            const severityConfig = getSeverityConfig(feedback.severity);
            const SeverityIcon = severityConfig.icon;

            return (
              <div
                key={index}
                className={`${severityConfig.bg} ${severityConfig.border} border rounded-lg p-3`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <div
                    className={`p-1.5 bg-white rounded-md border ${severityConfig.border}`}
                  >
                    <SeverityIcon
                      className={`w-4 h-4 ${severityConfig.iconColor}`}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {feedback.title}
                      </h3>

                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${severityConfig.bg} ${severityConfig.iconColor}`}
                      >
                        {feedback.severity}
                      </span>
                    </div>

                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {feedback.subject} • {feedback.chapter}
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-600 mb-2 ml-8 line-clamp-2">
                  {feedback.description}
                </p>

                <div className="ml-8 p-2 bg-white rounded-md border border-gray-200">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5" />

                    <div>
                      <span className="text-[10px] font-semibold text-gray-500 block mb-0.5">
                        Recommended Action
                      </span>

                      <p className="text-xs text-gray-700">{feedback.action}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RecommendationsFeedbacksSection;
